function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

import React from 'react';
import { context } from "../context";
import environment from "../environment";
import notifications from "../notifications";
import logger from "../logger";
import getResource from "../resources";
import object from "../utils/object";
import Translation from "./translation";
/**
 * Display editable translation component
 *
 * @param {string} key     - translation key
 * @param {number} number  - for pluralization
 *
 * @return {JSX.Element}
 */

export var t = function t(key, number) {
  return /*#__PURE__*/React.createElement(Translation, {
    number: number
  }, key);
};
/**
 * Translator service is used to fetch and sync API translations
 */

export default {
  /**
   * Languages storage
   *
   * @type {array}
   */
  languages: null,

  /**
   * Translation storage
   *
   * @type {object}
   *
   * @example {
   *   fr: {...}
   * }
   */
  translations: {},

  /**
   * Fetch translations for a specific locale
   *
   * @param {string}  locale
   * @param {boolean} force
   *
   * @return {Promise}
   */
  fetch: function fetch(locale, force) {
    var _this = this;

    if (this.translations[locale] !== undefined && !force) {
      return Promise.resolve();
    }

    return getResource('Translation').fetchByLocale(locale).then(function (translations) {
      _this.translations[locale] = translations;
      context.update({
        locale: locale
      });
    }).catch(function () {
      notifications.error(t('error_occurred'), /*#__PURE__*/React.createElement(React.Fragment, null, t('impossible_to_fetch_translation'), " : ", locale));
    });
  },

  /**
   *
   * @param {string} key
   * @param {number} number
   * @param {string} locale
   *
   * @return {string}
   */
  translate: function translate(key) {
    var number = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    var locale = arguments.length > 2 ? arguments[2] : undefined;
    var currentLocale = locale || this.getLocale();
    var translation = this.translations[currentLocale] && this.translations[currentLocale][key];

    if (_typeof(translation) !== 'object') {
      return key;
    } // eslint-disable-next-line no-nested-ternary


    return typeof number === 'number' && number > 1 ? translation.pluralValue ? translation.pluralValue.replace('$n', number) : translation.value.replace('$n', number) : translation.value.replace('$n', number);
  },

  /**
   * Get translation in storage
   *
   * @param {string} key
   * @param {string} locale
   *
   * @return {object|undefined}
   */
  getTranslation: function getTranslation(key, locale) {
    var currentLocal = locale || this.getLocale();
    return this.translations[currentLocal] && this.translations[currentLocal][key];
  },

  /**
   * Update translation in class storage
   *
   * @param {object} translation
   * @param {string} locale
   *
   * @return {self}
   */
  setTranslation: function setTranslation(translation, locale) {
    this.translations[locale] = translation;
    return this;
  },

  /**
   * Change current locale
   *
   * @param {string} locale
   * @return {Promise<void>}
   */
  setLocale: function setLocale(locale) {
    return this.fetch(locale).then(function () {
      context.update({
        locale: locale
      });
    });
  },

  /**
   * Get current locale
   *
   * @return {string}
   */
  getLocale: function getLocale() {
    return context.locale || environment.get('default_locale') || 'en';
  },

  /**
   *
   * @param force
   * @return {Promise<*>|Promise<array>}
   */
  fetchLanguages: function fetchLanguages(force) {
    var _this2 = this;

    if (this.languages !== null && !force) {
      return Promise.resolve(this.languages);
    }

    return getResource('Language').list({
      perPage: 1000
    }).then(function (data) {
      _this2.languages = data['hydra:member'];
    });
  },

  /**
   * Refresh language in class storage
   *
   * @param {object} language
   */
  refreshLanguage: function refreshLanguage(language) {
    if (this.languages === null) {
      this.languages = [language];
      return;
    }

    for (var i = 0, len = this.languages.length; i < len; ++i) {
      if (this.languages[i].id === language.id) {
        this.languages[i] = _objectSpread(_objectSpread({}, this.languages[i]), language);
        return;
      }
    }

    this.languages.push(language);
  },

  /**
   * Synchronize a translation with api
   *
   * @param {string}  key
   * @param {string}  syncValue - value or pluralValue
   * @param {string}  locale
   * @param {boolean} isPluralValue
   *
   * @return {Promise}
   */
  sync: function sync(key, syncValue, locale, isPluralValue) {
    var _this3 = this;

    var currentLocale = locale || this.getLocale();
    var translation = this.getTranslation(key, locale);

    var payload = _objectSpread(_objectSpread({}, translation), {}, _defineProperty({
      locale: currentLocale,
      key: key
    }, isPluralValue ? 'pluralValue' : 'value', syncValue));

    var isCreation = translation === undefined || translation.locale !== currentLocale;
    var verb = isCreation ? 'create' : 'update';

    if (isCreation) {
      payload.value = syncValue;
      payload.lang = object.get("@language.@id", this.translations[currentLocale]);
    }

    return getResource('Translation')[verb](payload).then(function (_ref) {
      var id = _ref.id,
          value = _ref.value,
          pluralValue = _ref.pluralValue;
      _this3.translations[currentLocale][key] = _objectSpread(_objectSpread({}, _this3.translations[currentLocale][key]), {}, {
        id: id,
        value: value,
        pluralValue: pluralValue,
        locale: currentLocale
      });
      context.update({
        locale: currentLocale
      });
      notifications.success('Success', /*#__PURE__*/React.createElement("div", {
        "aria-hidden": true
      }, "Translation \"".concat(key, "\" for locale \"").concat(currentLocale, "\" has been"), " ", /*#__PURE__*/React.createElement("strong", null, verb, "d"), isPluralValue && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("small", null, "You edited the plural value, please check simple value"))));
    }).catch(function () {
      notifications.error('Oops an error has occurred', "Impossible to ".concat(isCreation ? 'create' : 'update', " translation \"").concat(key, "\" for locale \"").concat(currentLocale, "\""));
    });
  }
};